import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Message,
  PhotoCamera,
  LocationOn,
  Schedule,
  Person,
  Pets,
} from '@mui/icons-material';

const ServiceUpdatesChat = ({ updates, currentUserId, ownerName, handlerName }) => {
  const getUpdateIcon = (updateType) => {
    switch (updateType) {
      case 'photo':
      case 'image':
        return <PhotoCamera sx={{ fontSize: 16 }} />;
      case 'message':
      case 'text':
        return <Message sx={{ fontSize: 16 }} />;
      case 'location':
        return <LocationOn sx={{ fontSize: 16 }} />;
      case 'start':
      case 'complete':
        return <Schedule sx={{ fontSize: 16 }} />;
      default:
        return <Message sx={{ fontSize: 16 }} />;
    }
  };

  const getUpdateTypeLabel = (updateType) => {
    switch (updateType) {
      case 'photo':
      case 'image':
        return 'Image';
      case 'message':
      case 'text':
        return 'Message';
      case 'location':
        return 'Location';
      case 'start':
        return 'Service Started';
      case 'complete':
        return 'Service Completed';
      default:
        return updateType.replace('_', ' ');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + 
             ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const renderUpdateContent = (update) => {
    // Use sent_by for alignment
    const isFromCurrentUser = update.sent_by?.id === currentUserId;
    const isSystem = update.update_type === 'start' || update.update_type === 'complete';
    const isFromHandler = update.sender_type === 'handler';

    // System messages (start/complete)
    if (isSystem) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          my: 2 
        }}>
          <Chip
            icon={getUpdateIcon(update.update_type)}
            label={getUpdateTypeLabel(update.update_type)}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      );
    }

    // Chat messages
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: isFromCurrentUser ? 'flex-end' : 'flex-start',
        mb: 2,
        px: 0
      }}>
        <Box sx={{ 
          maxWidth: '80%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isFromCurrentUser ? 'flex-end' : 'flex-start'
        }}>
          {/* Sender info */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 0.5,
            justifyContent: isFromCurrentUser ? 'flex-end' : 'flex-start'
          }}>
            {!isFromCurrentUser && (
              <Avatar sx={{ width: 24, height: 24, bgcolor: isFromHandler ? 'primary.main' : 'secondary.main' }}>
                {isFromHandler ? <Person sx={{ fontSize: 14 }} /> : <Pets sx={{ fontSize: 14 }} />}
              </Avatar>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {update.sent_by?.full_name || `${update.sent_by?.first_name || ''} ${update.sent_by?.last_name || ''}`.trim() || 'User'}
            </Typography>
            {isFromCurrentUser && (
              <Avatar sx={{ width: 24, height: 24, bgcolor: isFromHandler ? 'primary.main' : 'secondary.main' }}>
                {isFromHandler ? <Person sx={{ fontSize: 14 }} /> : <Pets sx={{ fontSize: 14 }} />}
              </Avatar>
            )}
          </Box>

          {/* Message bubble */}
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: isFromCurrentUser ? 'primary.main' : 'grey.200',
              color: isFromCurrentUser ? 'primary.contrastText' : 'text.primary',
              position: 'relative',
              minWidth: '150px',
              maxWidth: '100%',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&::before': isFromCurrentUser ? {
                content: '""',
                position: 'absolute',
                right: -8,
                top: 12,
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderLeft: '8px solid',
                borderLeftColor: 'primary.main'
              } : {
                content: '""',
                position: 'absolute',
                left: -8,
                top: 12,
                width: 0,
                height: 0,
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderRight: '8px solid',
                borderRightColor: 'grey.200'
              }
            }}
          >
            {/* Update type indicator */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              mb: update.message ? 1 : 0,
              opacity: 0.8
            }}>
              {getUpdateIcon(update.update_type)}
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                {getUpdateTypeLabel(update.update_type)}
              </Typography>
            </Box>

            {/* Message text */}
            {update.message && (
              <Typography variant="body1" sx={{ 
                fontSize: '0.9rem', 
                lineHeight: 1.4,
                wordBreak: 'break-word'
              }}>
                {update.message}
              </Typography>
            )}

            {/* Photo/Image */}
            {(update.photo || update.image) && (
              <Box sx={{ 
                mt: update.message ? 1 : 0,
                borderRadius: 2, 
                overflow: 'hidden'
              }}>
                <img 
                  src={update.photo || update.image} 
                  alt="Service update" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '200px',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            )}

            {/* Location info */}
            {update.latitude && update.longitude && (
              <Box sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'rgba(255,255,255,0.1)', 
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <LocationOn sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {update.latitude.toFixed(4)}, {update.longitude.toFixed(4)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Timestamp */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
            {formatTime(update.timestamp)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Chat header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Service Updates
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time updates from your service
        </Typography>
      </Box>

      {/* Messages */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 2,
        bgcolor: 'grey.50'
      }}>
        {updates.length > 0 ? (
          [...updates].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((update, index) => (
            <Box key={update.id || index} sx={{ mb: 3 }}>
              {renderUpdateContent(update)}
            </Box>
          ))
        ) : (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: 'text.secondary'
          }}>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              No updates yet
              <br />
              <Typography variant="caption">
                Updates will appear here when the service starts
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceUpdatesChat; 